import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Languages, Loader2, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuthStore } from "../stores/authStore";
import { useProfile } from "../services/meApi";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { LanguageMenuItems } from "./LanguageSwitcher";
import { ThemeIcon, ThemeMenuItems } from "./ThemeToggle";
import { TenantSwitcherMenu } from "./TenantSwitcherMenu";
import type { ProfileDropdownItem } from "./AppShell";

interface UserMenuProps {
  /** Items shown above the tenant switcher (profile, password, ...). */
  profileDropdownItems: ProfileDropdownItem[];
  handleLogout: () => Promise<void>;
  loggingOut: boolean;
  logoutError: string | null;
  /** Called after a tenant switch lands, to return the user to a safe route. */
  onNavigate: (path: string) => void;
}

/**
 * Topbar account menu — identity, per-user preferences (language, theme) and
 * session actions in one place.
 *
 * Language and theme live inside this menu rather than as standalone topbar
 * buttons: both are account-level preferences, and the topbar right edge is
 * reserved for context that changes per page.
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  profileDropdownItems,
  handleLogout,
  loggingOut,
  logoutError,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { data: profile } = useProfile();
  const { theme } = useTheme();
  const avatarUrl = profile?.avatarUrl ?? null;

  // Controlled so a tenant switch can keep the menu open through its async round
  // trip — TenantSwitcherMenu's radio items preventDefault() on select for exactly
  // this reason — and close it explicitly once the switch has landed.
  const [open, setOpen] = useState(false);

  const roleLabel =
    user?.role === "TenantAdmin"
      ? t("sidebar.role.tenantAdmin")
      : user?.role === "SuperAdmin"
        ? t("sidebar.role.superAdmin")
        : t("sidebar.role.member");

  const initials =
    user?.fullName
      ?.split(" ")
      .map((n: string) => n[0])
      .slice(0, 2)
      .join("") ?? "U";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 rounded-lg border border-border bg-transparent py-1 pl-1 pr-1.5 text-left transition-[background-color,border-color] duration-150",
            "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            open && "bg-accent",
          )}
          aria-label={user?.fullName ?? t("sidebar.unknownUser")}
        >
          <span
            className="size-7 shrink-0 overflow-hidden rounded-full bg-highlight flex items-center justify-center text-[0.6875rem] font-bold text-highlight-foreground"
            aria-hidden="true"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="size-full object-cover" />
            ) : (
              <span>{initials}</span>
            )}
          </span>
          <span className="max-lg:hidden min-w-0 max-w-36">
            <span className="block truncate text-[0.8125rem] font-semibold leading-tight text-foreground">
              {user?.fullName ?? t("sidebar.unknownUser")}
            </span>
            <span className="block truncate text-[0.6875rem] leading-tight text-muted-foreground">
              {roleLabel}
            </span>
          </span>
          <ChevronDown
            size={14}
            className="shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="w-64">
        <div className="px-2 py-1.5">
          <p className="truncate text-[0.8125rem] font-semibold text-foreground">
            {user?.fullName ?? t("sidebar.unknownUser")}
          </p>
          <p className="truncate text-[0.6875rem] text-muted-foreground">
            {user?.email ?? roleLabel}
          </p>
        </div>
        <DropdownMenuSeparator />

        {profileDropdownItems.length > 0 && (
          <>
            {profileDropdownItems.map((item) => (
              <DropdownMenuItem
                key={item.labelKey}
                className="gap-2"
                onClick={item.onClick}
                aria-label={t(item.labelKey)}
              >
                {item.icon}
                <span>{t(item.labelKey)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        <TenantSwitcherMenu
          memberships={user?.memberships ?? []}
          activeTenantId={user?.tenantId ?? null}
          onSwitched={() => {
            setOpen(false);
            onNavigate("/dashboard");
          }}
        />

        <DropdownMenuLabel className="text-[0.65rem] font-semibold uppercase tracking-widest text-muted-foreground">
          {t("topbar.userMenu.preferences")}
        </DropdownMenuLabel>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <Languages size={14} aria-hidden="true" />
            <span className="flex-1">{t("header.language.label")}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="min-w-36">
              <LanguageMenuItems />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <ThemeIcon theme={theme} size={14} />
            <span className="flex-1">{t("header.theme.label")}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="min-w-36">
              <ThemeMenuItems />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
          onClick={handleLogout}
          disabled={loggingOut}
          aria-label={
            loggingOut
              ? t("sidebar.loggingOut")
              : t("sidebar.profile.dropdown.logout")
          }
        >
          {loggingOut ? (
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <LogOut size={14} aria-hidden="true" />
          )}
          <span>
            {loggingOut
              ? t("sidebar.loggingOut")
              : t("sidebar.profile.dropdown.logout")}
          </span>
        </DropdownMenuItem>

        {logoutError && (
          <div className="px-2 pb-1.5 pt-1">
            <Alert variant="destructive" className="px-3 py-2 text-xs">
              <AlertDescription>{logoutError}</AlertDescription>
            </Alert>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
